import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space } from "antd";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import moment from "moment";

/**
 * Service Fee Line Item Component
 * Simplified version of LineItem for managing service fees (penalties/bonuses) in cleaning context
 * 
 * @param {object} cleaning - The cleaning object
 * @param {function} refreshParent - Optional callback to refresh parent component
 * @param {object} ref - Optional imperative handle used by the parent form
 */
const ServiceFeeLineItem = forwardRef(({ cleaning, refreshParent }, ref) => {
  const { t } = useTranslation();
  const size = "small";

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [lineItem, setLineItem] = useState(null);

  const [form] = Form.useForm();

  // Load existing line item if cleaning has anna_partner_line_item_id
  useEffect(() => {
    if (cleaning?.anna_partner_line_item_id && cleaning?.anna_partner_line_item) {
      const lineItem = cleaning.anna_partner_line_item;

      if (lineItem.service_fee_impact === "increase" || lineItem.service_fee_impact === "decrease") {
        setLineItem(lineItem);
        setSelectedInvoiceId(lineItem.invoice_id);
      } else {
        setLineItem(null);
      }
    } else {
      setLineItem(null);
    }
  }, [cleaning?.anna_partner_line_item_id, cleaning?.anna_partner_line_item]);

  // Load available service invoices
  const loadAvailableInvoices = () => {
    if (!cleaning?.id) return;

    setLoadingInvoices(true);
    const cleaningType = cleaning.is_recurring ? "recurring" : "one-time";
    
    coreApi
      .get(`/cleanings/${cleaningType}/${cleaning.id}/available-service-invoices-all`)
      .then((response) => {
        setAvailableInvoices(response.data.invoices || []);
        
        // Auto-select first invoice if none selected
        if (!selectedInvoiceId && response.data.invoices?.length > 0) {
          setSelectedInvoiceId(response.data.invoices[0].id);
        }
      })
      .catch((error) => {
        notification.error({
          message: t("cleanings.serviceFee.errorLoadingInvoices"),
          description: error?.response?.data?.message,
        });
      })
      .finally(() => {
        setLoadingInvoices(false);
      });
  };

  const handleAddNew = () => {
    setIsEditing(true);
    loadAvailableInvoices();

    // Get building address - support both nested object and flat structure
    const street = cleaning?.building_unit?.street || cleaning?.building_unit_street || '';
    const houseNumber = cleaning?.building_unit?.house_number || cleaning?.building_unit_house_number || '';
    const buildingAddress = street && houseNumber ? `${street} ${houseNumber}` : '';

    const countryCode = cleaning?.building_unit?.building?.city_branch?.country_branch?.country_code_iso_3166 || 'CZ';

    let defaultContent = '';
    switch (countryCode) {
      case 'SK':
        defaultContent = `Administratívny poplatok (${buildingAddress})`;
        break;
      case 'GB':
      case 'UK':
      case 'US':
        defaultContent = `Fee increase – cleaning not performed according to the contract (${buildingAddress})`;
        break;
      case 'CZ':
      default:
        defaultContent = `Administrativní poplatek (${buildingAddress})`;
        break;
    }
    
    form.setFieldsValue({
      content: defaultContent,
      service_fee_impact: "increase",
      total_amount: null,
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    loadAvailableInvoices();
    form.setFieldsValue({
      content: lineItem?.content,
      service_fee_impact: lineItem?.service_fee_impact || "increase",
      total_amount: lineItem?.total_amount || 0,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
  };

  const validateTotalAmount = (_, value) => {
    const serviceFeeImpact = form.getFieldValue("service_fee_impact");

    if (!serviceFeeImpact || serviceFeeImpact === "none") {
      return Promise.resolve();
    }

    if (value === 0 || value === null || value === undefined) {
      return Promise.reject(new Error(t("finance.lineItems.validation.listPrice")));
    }

    if (serviceFeeImpact === "increase" && value <= 0) {
      return Promise.reject(new Error(t("finance.lineItems.validation.totalAmountIncrease")));
    }

    if (serviceFeeImpact === "decrease" && value >= 0) {
      return Promise.reject(new Error(t("finance.lineItems.validation.totalAmountDecrease")));
    }

    return Promise.resolve();
  };

  const handleValuesChange = (changedValues) => {
    if (changedValues.service_fee_impact) {
      form.validateFields(['total_amount']);

      // Get building address - support both nested object and flat structure
      const street = cleaning?.building_unit?.street || cleaning?.building_unit_street || '';
      const houseNumber = cleaning?.building_unit?.house_number || cleaning?.building_unit_house_number || '';
      const buildingAddress = street && houseNumber ? `${street} ${houseNumber}` : '';
      
      // Get country code from building's city branch
      const countryCode = cleaning?.building_unit?.building?.city_branch?.country_branch?.country_code_iso_3166 || 'CZ';
      
      let defaultContent = '';
      
      if (changedValues.service_fee_impact === 'increase') {
        switch (countryCode) {
          case 'SK':
            defaultContent = `Administratívny poplatok (${buildingAddress})`;
            break;
          case 'GB':
          case 'UK':
          case 'US':
            defaultContent = `Fee increase – cleaning not performed according to the contract (${buildingAddress})`;
            break;
          case 'CZ':
          default:
            defaultContent = `Administrativní poplatek (${buildingAddress})`;
            break;
        }
      } else if (changedValues.service_fee_impact === 'decrease') {
        switch (countryCode) {
          case 'SK':
            defaultContent = `Zníženie poplatku (${buildingAddress})`;
            break;
          case 'GB':
          case 'UK':
          case 'US':
            defaultContent = `Fee decrease (${buildingAddress})`;
            break;
          case 'CZ':
          default:
            defaultContent = `Snížení poplatku (${buildingAddress})`;
            break;
        }
      }
      
      form.setFieldsValue({
        content: defaultContent,
      });
    }
  };

  const savePending = (values, refresh = true) => {
    if (!selectedInvoiceId) {
      notification.error({
        message: t("cleanings.serviceFee.selectInvoiceRequired"),
      });

      return Promise.reject(new Error("missing invoice"));
    }

    setSubmitting(true);

    const totalAmount = values.total_amount || 0;
    const vatPercentage = 0.21;
    const listPrice = totalAmount / (1 + vatPercentage);
    const vatAmount = totalAmount - listPrice;

    const lineItemData = {
      invoice_id: selectedInvoiceId,
      content: values.content,
      service_fee_impact: values.service_fee_impact,
      quantity: 1,
      unit_price: totalAmount,
      list_price: listPrice,
      discount_amount: 0,
      vat_percentage: vatPercentage,
      vat_amount: vatAmount,
      total_amount: totalAmount,
    };

    const request = lineItem?.id
      ? coreApi.put(`/line-items/${lineItem.id}`, lineItemData)
      : coreApi.post("/line-items", lineItemData);

    return request
      .then((response) => {
        const savedLineItem = response.data.line_item
          || response.data.invoice?.line_items?.find((item) => item.id === lineItem?.id)
          || response.data.invoice?.line_items?.find(
            (item) =>
              item.invoice_id === selectedInvoiceId &&
              item.content === values.content &&
              item.service_fee_impact === values.service_fee_impact &&
              Number(item.total_amount) === Number(values.total_amount)
          );

        if (!savedLineItem) {
          notification.error({
            message: t("cleanings.serviceFee.errorSaving"),
          });

          return Promise.reject(new Error("line item not returned"));
        }

        notification.success({
          message: lineItem?.id
            ? t("cleanings.serviceFee.updateSuccess")
            : t("cleanings.serviceFee.createSuccess"),
        });

        if (lineItem?.id) {
          setLineItem(savedLineItem);
          setIsEditing(false);

          if (refresh && refreshParent) {
            refreshParent();
          }

          return savedLineItem;
        }

        const cleaningType = cleaning.is_recurring ? "recurring" : "one-time";

        return coreApi
          .put(`/cleanings/${cleaningType}/${cleaning.id}/line-item`, {
            anna_partner_line_item_id: savedLineItem.id,
          })
          .then(() => {
            setLineItem(savedLineItem);
            setIsEditing(false);

            if (refresh && refreshParent) {
              refreshParent();
            }

            return savedLineItem;
          })
          .catch((error) => {
            notification.error({
              message: t("cleanings.serviceFee.errorUpdatingCleaning"),
              description: error?.response?.data?.message,
            });

            error.serviceFeeNotified = true;

            return Promise.reject(error);
          });
      })
      .catch((error) => {
        if (error?.response && !error.serviceFeeNotified) {
          notification.error({
            message: t("cleanings.serviceFee.errorSaving"),
            description: error?.response?.data?.message,
          });
        }

        return Promise.reject(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = (values) => {
    savePending(values).catch(() => {
      // notified above, the block stays open so the user can retry
    });
  };

  /**
   * Imperative API for a surrounding form: lets its Save button flush the fee
   * instead of silently dropping what the user typed in here.
   */
  useImperativeHandle(
    ref,
    () => ({
      hasPendingChanges: () => {
        if (!isEditing) {
          return false;
        }

        const totalAmount = form.getFieldValue("total_amount");

        return totalAmount !== null && totalAmount !== undefined && totalAmount !== "";
      },
      validatePending: () => form.validateFields(),
      savePending: (values) => savePending(values, false),
    }),

    [isEditing, form, selectedInvoiceId, lineItem, cleaning]
  );

  const handleDelete = () => {
    if (!lineItem?.id) return;

    setSubmitting(true);
    coreApi
      .delete(`/line-items/${lineItem.id}`)
      .then(() => {
        notification.success({
          message: t("cleanings.serviceFee.deleteSuccess"),
        });

        // Update cleaning to remove line_item_id reference using dedicated endpoint
        const cleaningType = cleaning.is_recurring ? "recurring" : "one-time";
        coreApi
          .put(`/cleanings/${cleaningType}/${cleaning.id}/line-item`, {
            anna_partner_line_item_id: null,
          })
          .then(() => {
            setLineItem(null);
            setIsEditing(false);
            form.resetFields();
            if (refreshParent) {
              refreshParent();
            }
          });
      })
      .catch((error) => {
        notification.error({
          message: t("cleanings.serviceFee.errorDeleting"),
          description: error?.response?.data?.message,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (lineItem && !isEditing) {
    return (
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col span={24}>
          <strong>{t("cleanings.serviceFee.title")}:</strong>
        </Col>
        <Col span={12}>
          <div style={{ fontSize: "12px", color: "#666" }}>{t("cleanings.serviceFee.content")}:</div>
          <div>{lineItem.content}</div>
        </Col>
        <Col span={6}>
          <div style={{ fontSize: "12px", color: "#666" }}>{t("cleanings.serviceFee.impact")}:</div>
          <div>
            {lineItem.service_fee_impact === "increase" && (
              <span style={{ color: "#B92F46" }}>{t("finance.lineItems.serviceFeeImpactOptions.increase")}</span>
            )}
            {lineItem.service_fee_impact === "decrease" && (
              <span style={{ color: "#4CAF50" }}>{t("finance.lineItems.serviceFeeImpactOptions.decrease")}</span>
            )}
          </div>
        </Col>
        <Col span={6}>
          <div style={{ fontSize: "12px", color: "#666" }}>{t("cleanings.serviceFee.amount")}:</div>
          <div>
            <strong>{lineItem.total_amount}</strong>
          </div>
        </Col>
        <Col span={24}>
          <Space>
            <Button size={size} icon={<EditOutlined />} onClick={handleEdit}>
              {t("common.edit")}
            </Button>
            <Button size={size} danger icon={<DeleteOutlined />} onClick={handleDelete} loading={isSubmitting}>
              {t("common.delete")}
            </Button>
          </Space>
        </Col>
      </Row>
    );
  }

  if (isEditing) {
    return (
      <div style={{ marginTop: 8 }}>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <strong>{lineItem ? t("cleanings.serviceFee.editTitle") : t("cleanings.serviceFee.addTitle")}</strong>
          </Col>
          
          <Col span={24}>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: 4 }}>
                {t("cleanings.serviceFee.selectInvoice")}
              </div>
              <Select
                size={size}
                loading={loadingInvoices}
                value={selectedInvoiceId}
                onChange={setSelectedInvoiceId}
                placeholder={t("cleanings.serviceFee.selectInvoicePlaceholder")}
                style={{ width: "100%" }}
              >
                {availableInvoices.map((invoice) => {
                  let displayName = ' - ';
                  
                  if (invoice.contractor_name && invoice.worker_name) {
                    displayName = `${invoice.contractor_name} → ${invoice.worker_name}`;
                  } else if (invoice.partner_name) {
                    displayName = invoice.partner_name;
                  }

                  return (
                    <Select.Option key={invoice.id} value={invoice.id}>
                      {invoice.invoice_number} - {displayName} - {moment(invoice.date_issued).format("DD.MM.YYYY")} ({invoice.currency})
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </Col>
        </Row>

        <Form form={form} onFinish={handleSubmit} onValuesChange={handleValuesChange} layout="vertical" component={false}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Form.Item
                name="content"
                label={t("cleanings.serviceFee.content")}
                rules={[{ required: true, message: t("finance.lineItems.validation.content") }]}
              >
                <Input.TextArea autoSize size={size} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="service_fee_impact"
                label={t("cleanings.serviceFee.impact")}
                rules={[{ required: true }]}
              >
                <Select size={size}>
                  <Select.Option value="increase">{t("finance.lineItems.serviceFeeImpactOptions.increase")}</Select.Option>
                  <Select.Option value="decrease">{t("finance.lineItems.serviceFeeImpactOptions.decrease")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="total_amount"
                label={t("cleanings.serviceFee.amount")}
                rules={[{ required: true }, { validator: validateTotalAmount }]}
              >
                <InputNumber precision={2} size={size} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  size={size}
                  icon={<CheckOutlined />}
                  onClick={() => form.submit()}
                  loading={isSubmitting}
                >
                  {t("common.save")}
                </Button>
                <Button size={size} icon={<StopOutlined />} onClick={handleCancel}>
                  {t("common.cancel")}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }

  // Add new button
  return (
    <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
      <Col span={24}>
        <Button size={size} type="dashed" icon={<PlusOutlined />} onClick={handleAddNew} block>
          {t("cleanings.serviceFee.addButton")}
        </Button>
      </Col>
    </Row>
  );
});

ServiceFeeLineItem.displayName = "ServiceFeeLineItem";

export default ServiceFeeLineItem;
